import React, { CSSProperties, ReactNode } from 'react'
import styled from '@emotion/styled'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { colors, font, spacing } from '@mobi/component-library/Foundations'

export type AccountHeaderProps = {
  alignItems: 'start' | 'center' | 'end'
  title: string | ReactNode
  description?: string | ReactNode
  onClose?: () => void
  isCloseDisabled?: boolean
  style?: CSSProperties
}

export const AccountHeader = ({
  alignItems,
  title,
  description,
  onClose,
  isCloseDisabled,
  style,
}: AccountHeaderProps) => {
  return (
    <AccountHeaderWrapper alignItems={alignItems} onClose={onClose} style={style}>
      {onClose && (
        <ButtonWrapper>
          <Button
            aria-label='Back button'
            color='secondary_grey'
            isIconOnlyButton={true}
            onClick={onClose}
            size='xs'
            disabled={isCloseDisabled}
          >
            <Icon name='SolidChevronLeft' />
          </Button>
        </ButtonWrapper>
      )}
      <TextWrapper alignItems={alignItems}>
        <MainTitle>{title}</MainTitle>
        {description && <Description>{description}</Description>}
      </TextWrapper>
    </AccountHeaderWrapper>
  )
}

const AccountHeaderWrapper = styled.header<Pick<AccountHeaderProps, 'alignItems' | 'onClose'>>(
  ({ onClose, alignItems }) => ({
    flex: '0 0 10vh',
    display: onClose ? 'grid' : 'flex',
    gridTemplateColumns: onClose ? '1fr 10fr 1fr' : 'auto',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: alignItems,
    textAlign: getTextAlign(alignItems),
    margin: `0 0 ${spacing.md}`,
    a: {
      color: 'inherit',
    },
  })
)

const TextWrapper = styled.div<Pick<AccountHeaderProps, 'alignItems'>>(({ alignItems }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: alignItems,
  textAlign: getTextAlign(alignItems),
  width: '100%',
}))

const ButtonWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  marginRight: spacing.smx1,
})

export const MainTitle = styled.div({
  color: colors.black,
  ...font.size.xl4,
  fontFamily: font.family.primary,
  fontWeight: font.weight.semibold,
})

export const Description = styled.div({
  fontFamily: font.family.primary,
  color: colors.neutral[800],
  marginTop: spacing.smx1,
  ...font.size.lg,
})

const getTextAlign = (alignItems: 'start' | 'center' | 'end'): 'left' | 'center' | 'right' => {
  if (alignItems === 'start') return 'left'
  if (alignItems === 'end') return 'right'
  return 'center'
}
